// Skills v2 — Quality & Improve (unified closed loop) pure formatting logic.
//
// The mockup is the conformance spec: docs/design/skills-framework-v2-mockup.html
// <section id="s7">. This module shapes the three data sources — the L1
// multi-signal quality summary, the L2 improvement proposals, and the per-skill
// health rollup — into the exact cards the screen renders. Zero React, fully
// unit-testable; QualitySection stays a thin renderer.

import { toOptions } from "../../../components/comboboxLogic";

// ── Proposal classification ──────────────────────────────────────────
// A proposal is either "applied for you" (the loop applied it automatically —
// high confidence, low risk), "awaiting your okay" (sensitive → needs approval),
// or a terminal/other state we don't surface as an action card.
const APPLIED_STATUSES = new Set(["applied", "auto_applied", "approved"]);
const AWAITING_STATUSES = new Set(["awaiting_approval", "drafted", "pending"]);

export function proposalKind(p) {
  const s = String(p?.status || "").toLowerCase();
  if (APPLIED_STATUSES.has(s)) return "applied";
  if (AWAITING_STATUSES.has(s)) return "awaiting";
  return "other"; // rejected | eval_failed | superseded → not an action card
}

// eval_delta is a fraction (0.09) → whole-percent integer (9). null when absent.
export function deltaPct(p) {
  const d = Number(p?.eval_delta);
  if (!Number.isFinite(d)) return null;
  return Math.round(d * 100);
}

// A human, layer-blind headline for a proposal — outcome-first, never "L2b".
// The developer-view note (target_layer + payload) carries the mechanics.
const LAYER_NOUN = {
  skills: "skill", prompt: "prompt", model: "model",
};
export function proposalHeadline(p) {
  const trig = String(p?.trigger || "").toLowerCase();
  const diag = (p?.diagnosis || "").trim();
  if (diag) return diag;
  if (trig === "decay") return "A skill's lift is slipping";
  if (trig === "correction") return "Answers can be improved";
  return "Suggested improvement";
}

// The one-line "why you decide this one" rationale for an awaiting proposal,
// derived from the risk + risk_factors. High/medium risk = sensitive.
export function whyYouDecide(p) {
  const factors = Array.isArray(p?.risk_factors) ? p.risk_factors : [];
  const pretty = factors
    .map((f) => String(f).replace(/_/g, " "))
    .filter(Boolean);
  if (pretty.length) {
    return `it touches ${pretty.join(" + ")} — a sensitive area — so we don't apply it automatically.`;
  }
  const risk = String(p?.risk || "").toLowerCase();
  if (risk === "high" || risk === "medium") {
    return "it changes customer-facing behaviour — a sensitive area — so we don't apply it automatically.";
  }
  return "we hand this one to you to approve before it goes live.";
}

// Developer-view one-liner: the layer + what the payload adjusts.
export function developerNote(p) {
  const noun = LAYER_NOUN[String(p?.target_layer || "").toLowerCase()] || "config";
  const payload = p?.draft_payload || {};
  const skill = payload.skill_name || payload.base_name || payload.name;
  const change = payload.change || payload.summary || payload.kind;
  if (skill && change) return `${change} on the ${skill} ${noun}.`;
  if (skill) return `adjusts the ${skill} ${noun}.`;
  if (change) return `${change} (${noun} layer).`;
  return `adjusts the ${noun} layer.`;
}

// ── Per-skill health rollup (from GET /skills/your-skills, filtered) ──
// Each row: { name, liftPct, health, status, needsReview }. health maps the
// backend validation rollup to the mockup's status vocabulary.
const HEALTH_LABEL = {
  healthy: { label: "Healthy", tone: "ok" },
  needs_look: { label: "Lift decayed", tone: "warn" },
  paused: { label: "Paused", tone: "paused" },
  new: { label: "New", tone: "review" },
};
export function perSkillRows(yourSkills, agentId) {
  const list = Array.isArray(yourSkills) ? yourSkills : [];
  const rows = agentId
    ? list.filter((s) => Array.isArray(s.agent_ids) && s.agent_ids.includes(agentId))
    : list;
  return rows.map((s) => {
    const h = HEALTH_LABEL[s.health] || HEALTH_LABEL.new;
    return {
      name: s.display_name || s.base_name || "Skill",
      liftPct: (s.impact_pct == null || !Number.isFinite(Number(s.impact_pct))) ? null : Number(s.impact_pct),
      health: s.health,
      status: h.label,
      tone: h.tone,
      needsReview: s.health === "needs_look" || s.health === "paused",
    };
  });
}

// ── Headline metrics (the 4 metric tiles) ────────────────────────────
// Derived honestly from what the endpoints give us:
//   avgLift  — mean of per-skill lift (this agent), null when none measured.
//   healthy  — [healthyCount, total] across this agent's skills.
//   applied  — count of proposals the loop applied for you (auto).
//   awaiting — count of proposals awaiting your okay.
function within30d(iso, now) {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  return (now - t) <= 30 * 24 * 3600 * 1000;
}

export function deriveMetrics({ perSkill, proposals, now }) {
  const skills = Array.isArray(perSkill) ? perSkill : [];
  const props = Array.isArray(proposals) ? proposals : [];
  const nowMs = Number.isFinite(now) ? now : 0;

  const lifts = skills.map((s) => s.liftPct).filter((v) => Number.isFinite(v));
  const avgLift = lifts.length ? Math.round(lifts.reduce((a, b) => a + b, 0) / lifts.length) : null;

  const healthyCount = skills.filter((s) => s.health === "healthy").length;

  const applied = props.filter(
    (p) => proposalKind(p) === "applied" && (nowMs === 0 || within30d(p.applied_at, nowMs)),
  ).length;
  const awaiting = props.filter((p) => proposalKind(p) === "awaiting").length;

  return {
    avgLift,
    healthy: [healthyCount, skills.length],
    applied,
    awaiting,
  };
}

// ── Small display helpers ────────────────────────────────────────────
export function signPct(n) {
  if (!Number.isFinite(n)) return "—";
  return `${n >= 0 ? "▲ +" : "▼ "}${Math.abs(n)}%`;
}

// A compact relative "…ago" from an ISO timestamp (pass now in ms for tests).
export function timeAgo(iso, now) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t) || !Number.isFinite(now)) return "recently";
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// ── Actionable empty-state (next_actions from quality-summary) ────────
// The backend explains WHY the quality surface is starved as a list of
// { code, label, detail } (codes: enable_fact_check | pending_csat |
// attach_skill). Normalize defensively — drop entries with no usable label,
// coerce every field to a trimmed string — so the empty-state can render each
// as "label (bold) — detail (muted)". Returns [] when nothing is actionable, so
// the caller keeps its plain "not enough feedback yet" copy.
export function nextActionRows(nextActions) {
  const list = Array.isArray(nextActions) ? nextActions : [];
  return list
    .filter((a) => a && a.label != null && String(a.label).trim() !== "")
    .map((a) => ({
      code: a.code == null ? "" : String(a.code),
      label: String(a.label).trim(),
      detail: a.detail == null ? "" : String(a.detail).trim(),
    }));
}

// ── "Run a check now" dataset picker ─────────────────────────────────
// Combobox options for the dataset picker — datasets as {id,label}, with the
// agent's suggested dataset merged in first (so its label renders even if the
// list call hasn't returned that row yet). Deduped by id.
export function datasetOptions(datasets, suggested) {
  const opts = toOptions(datasets, ["name", "label"]);
  const sid = suggested && suggested.id != null ? String(suggested.id) : null;
  if (sid && !opts.some((o) => String(o.id) === sid)) {
    opts.unshift({ id: suggested.id, label: suggested.name || suggested.id, raw: suggested });
  }
  return opts;
}

// The dataset the picker should DEFAULT to. Order of preference:
//   1. `current` — a value the user (or a prior default) already chose; never override it.
//   2. `suggested` — the agent's most-recent eval-run dataset (suggested_dataset).
//   3. the first available dataset.
//   4. "" when there's nothing to pick.
// The suggested id wins even if the list hasn't loaded it yet — datasetOptions
// merges it in so it still renders a label.
export function resolveDefaultDataset(suggested, datasets, current) {
  if (current) return current;
  const sid = suggested && suggested.id != null ? String(suggested.id) : null;
  if (sid) return sid;
  const list = Array.isArray(datasets) ? datasets : [];
  return (list[0] && list[0].id) || "";
}

// True when the current dataset selection IS the suggested one — drives the
// subtle "defaulted to this agent's eval set" hint.
export function isSuggestedDefault(suggested, runDataset) {
  const sid = suggested && suggested.id != null ? String(suggested.id) : null;
  return !!sid && sid === runDataset;
}

// A short deterministic sparkline series (6 bars, 0..1) for a metric — derived
// from a seed number so it's stable across renders (no Math.random). Decorative.
export function sparkSeries(seed) {
  const base = Number.isFinite(seed) ? Math.abs(seed) : 5;
  const out = [];
  let v = 0.4 + ((base % 5) / 10);
  for (let i = 0; i < 6; i++) {
    v = Math.min(0.95, Math.max(0.25, v + (((base + i * 7) % 11) - 5) / 30));
    out.push(Number(v.toFixed(2)));
  }
  return out;
}
